import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApiService } from '@services/api/api.service';
import { ProjectService } from '@services/api/project.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ReduxService } from '@state-management/redux.service';
import { ToastrService } from 'ngx-toastr';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

@Component({
  selector: 'app-developer-agreement',
  templateUrl: './developer-agreement.component.html',
  styleUrls: ['./developer-agreement.component.scss'],
})
export class DeveloperAgreementComponent {
  @Output() is_agreement = new EventEmitter<boolean>();
  @Input() agree: any;

  public agreement_Form: FormGroup;
  public projectId: any;
  agencyList: any[] = [];
  internalAgentList: any[] = [];
  agreement_type: any[] = [];
  agreement_status: any[] = [];
  btn_disabled = false;
  constructor(
    private Api: ApiService,
    private toast: ToastrService,
    public _projectService: ProjectService,
    private localStorageService: LocalStorageService,
    public lmService: LeadManagementService,
    private fb: FormBuilder,
    private redux: ReduxService,
    private remmiConfirm: RemmiConfirmService
  ) {
    this.agreement_Form = this.fb.group({
      project_id: null,
      agency: null,
      agent: null,
      internal_agent: null,
      agreement_type: null,
      agreement_status: null,
      agreement_sent: null,
      agreement_exe_date: null,
      comments: null,
      flat_fee: null,
      commission_comments: null,
      commission: null,
      bonus: null,
      agreement_cancelled: null,
    });
  }

  // Lifecycle hook that runs when the component initializes
  ngOnInit(): void {
    // Fetch project list and short contacts from lmService
    this.lmService.GetProjectList();
    // this.lmService.getShortContacts();

    // Filter the agency list to include only developers
    // this.agencyList = this.lmService.shortCompany?.filter(el => el?.company_type?.toLowerCase() == 'developer');

    // Fetch office users and project tags
    this.getAgencyList();
    this.getOfficeUsers();
    this.getTags();

    // Retrieve project ID from local storage
    let pid = Crypto.decrypt(this.localStorageService.GetItem('pid'));
    if (pid !== null && pid !== undefined) {
      this.projectId = pid;

      // If there is no associated project in agreement, patch the project ID in the form
      if (!this.agree?.project) {
        this.agreement_Form.patchValue({
          project_id: pid,
        });
      }
    }

    // Fetch agreement details
    this.getAgreement();
  }
  getAgencyList() {
    let office_id = this.Api.getOfficeId();
    let user_id = this.Api.getUserId();
    let url = `/contacts/list?office_id=${office_id}&uid=${user_id}&type=All&page=1&per_page=990&user_type=Contact&company_type=${JSON.stringify(['Developer'])}`;
    this.Api.get(url).subscribe({
      next: (response: any) => {
        this.agencyList = response?.data;
      },
    });
  }

  // Fetches agreement details for the selected project
  getAgreement() {
    if (this.agree?._id) {
      this._projectService.getProjectAgreementById(this.agree?._id).subscribe({
        next: (response: any) => {
          // Patch the agreement details into the form
          this.agreement_Form.patchValue(response?.data);
          this.agreement_Form.patchValue({
            agreement_sent: response?.data?.agreement_sent ? new Date(response?.data?.agreement_sent) : null,
            agreement_exe_date: response?.data?.agreement_exe_date ? new Date(response?.data?.agreement_exe_date) : null,
          });
        },
      });
    }
  }

  // Event emitter to handle library data updates
  libraryDataEmitter: [] = [];
  GetLibraryData(event) {
    this.libraryDataEmitter = event;
  }

  // Deletes an agreement and resets the form
  deleteAgreement() {
    this.remmiConfirm
      .delete({ title: 'Delete Agreement?', message: 'This developer agreement will be permanently deleted.' })
      .subscribe(isConfirmed => {
        if (!isConfirmed) return;
        this._projectService.deleteProjectAgreement(this.agree?._id).subscribe({
          next: () => {
            this.toast.success('Deleted Successfully');
            this.agree = null;
            this.agreement_Form.reset();
            this.is_agreement.emit(true);
          },
        });
      });
  }

  // Cancels the agreement by resetting the form
  cancelAgreement() {
    this.agree = null;
    this.agreement_Form.reset();
    this.is_agreement.emit(true);
  }

  inserted_id = null;

  // Saves the agreement (creates a new one or updates an existing one)
  saveAgreement(param) {
    let form = this.agreement_Form.value;

    // Set required fields before making the API request
    form['office_id'] = this.Api.getOfficeId();
    form['user_id'] = this.Api.getUserId();
    form['is_developer'] = true;

    if (this.inserted_id) {
      // Update existing agreement if inserted_id is set
      this._projectService.updateProjectAgreement(this.inserted_id, form).subscribe({
        next: () => {
          this.btn_disabled = false;
          this.toast.success('Agreement updated successfully');
          if (param == 'create_close') {
            this.agree = null;
            this.inserted_id = null;
            this.agreement_Form.reset();
            this.is_agreement.emit(true);
          }
        },
      });
    } else {
      if (this.agree?._id) {
        // Update an existing agreement using its ID
        this._projectService.updateProjectAgreement(this.agree?._id, form).subscribe({
          next: () => {
            this.toast.success('Agreement updated successfully');
            this.btn_disabled = false;
            if (param == 'create_close') {
              this.agree = null;
              this.agreement_Form.reset();
              this.is_agreement.emit(true);
            }
          },
        });
      } else {
        // Create a new agreement
        this._projectService.saveProjectAgreement(form).subscribe({
          next: (response: any) => {
            this.btn_disabled = false;
            this.toast.success('Agreement added successfully');
            this.inserted_id = response?.insertedId;
            if (param == 'create_close') {
              this.agreement_Form.reset();
              this.is_agreement.emit(true);
            }
          },
        });
      }
    }
  }

  tagsData: any[] = [];

  // Fetches tags related to the project
  getTags() {
    this.redux.getTags(this.Api.getOfficeId(), false, 'project').subscribe({
      next: response => {
        this.tagsData = [...response?.data];

        // Process and categorize tags based on their type
        this.tagsData[0]?.types.forEach(tag => {
          if (tag?.slug == 'agreement-type') {
            this.agreement_type = [...tag?.data]?.sort((a, b) => a?.tag_name.localeCompare(b?.tag_name));
          }
          if (tag?.slug == 'agreement-status') {
            this.agreement_status = [...tag?.data]?.sort((a, b) => a?.tag_name.localeCompare(b?.tag_name));
          }
        });
      },
    });
  }

  public role_id = '';
  public Users: any[];

  // Fetches office users based on the provided filters
  getOfficeUsers(uid = this.Api.getUserId(), officeid = this.Api.getOfficeId()) {
    let url = `/users/office-staff-list?uid=${uid}&officeid=${officeid}&show=staff`;
    // Fetch office staff details and sort them by full name
    this.redux.getSetProjectInfo(false, 'office_staff', url).subscribe({
      next: response => {
        const { status, data } = response;
        if (status) {
          // Exclude users with the role "Master Admin"
          this.Users = data?.filter(u => {
            return u.role !== 'Master Admin';
          });
        }
      },
    });
  }

  // Fetches internal agents associated with the project
  getInternalAgent() {
    this._projectService.getAssociation(this.projectId).subscribe({
      next: response => {
        let internal = [];
        let saleManager = [];
        let agency = [];

        // Filter external companies from shortCompany list
        this.lmService.shortCompany.forEach(n => {
          if (response?.['data']?.externals_company?.includes(n._id)) {
            if (!agency?.find(el => el?._id == n._id)) {
              agency.push({ _id: n._id, full_name: n.full_name, email: n.email, company: 'Yes' });
            }
          }
        });

        this.agencyList = agency;

        // Filter internal and sales manager users
        this.Users?.forEach(n => {
          if (response?.['data']?.internals_users.includes(n._id)) {
            if (!internal?.find(el => el?._id == n._id)) {
              internal.push({ _id: n._id, full_name: n.full_name, email: n.email, company: 'Yes' });
            }
          }
          if (response?.['data']?.salesmanagers_users.includes(n._id)) {
            if (!saleManager?.find(el => el?._id == n._id)) {
              saleManager.push({ _id: n._id, full_name: n.full_name, email: n.email, company: 'Yes' });
            }
          }
        });

        // Sort internal agents list by full name
        this.internalAgentList = [...internal, ...saleManager].sort((a, b) => a?.full_name.localeCompare(b?.full_name));

        // Refresh agreement details
        this.getAgreement;
      },
    });
  }
}
